import { useFormContext, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Input, Select } from 'components/atoms';
import CitySelect from 'components/molecules/CitySelect/CitySelect';
import CountrySelect from 'components/molecules/CountrySelect/CountrySelect';
import DepartmentSelect from 'components/molecules/DepartmentSelect/DepartmentSelect';
import { useUpdateContactInfoHiringMutation } from 'services/talento';
import { useExternalCodeContext } from 'utils/helpers/ExternalCodesContext';
import { IOption } from 'utils/interfaces';

import { IAdminPanelForm } from '../AdminPanelForm.schema';

import styles from './ContactDataSection.module.scss';
interface IProps {
  defaultCountry?: string;
  defaultState?: string;
  defaultCity?: string;
}
export default function ContactDataSection({ defaultCountry, defaultState, defaultCity }: IProps) {
  const { control, getValues, setValue, trigger } = useFormContext<IAdminPanelForm>();
  setValue('addressType', 'Domicilio');
  setValue('phone', getValues('phone')?.replace('+', ''));
  const { email } = useParams();
  const [updateContactInfoHiring] = useUpdateContactInfoHiringMutation();
  const {
    countryOfResidence,
    departmentOfResidence,
    countryStatesCode,
    countryCitiesCode,
    isErrorCountryController,
    setCityOfResidence,
    setCountryOfResidence,
    setDepartmentOfResidence,
  } = useExternalCodeContext();
  const options: IOption[] = [{ value: 'Domicilio', label: 'Domicilio' }];

  async function submitData() {
    const {
      phone,
      address,
      addressType,
      cityOfResidence,
      complementaryAddresses,
      countryOfResidence,
      departamentOfResidence,
      countryOfBirthEn,
      state,
      countryOfResidenceISO,
    } = getValues();
    const phoneFormatted = phone?.replace('+', '') ?? '';
    await updateContactInfoHiring({
      address,
      addressType,
      cityOfResidence,
      complementaryAddresses: complementaryAddresses ?? '',
      countryOfResidence,
      departamentOfResidence,
      email: email ?? '',
      phone: phoneFormatted,
      countryOfBirthEn,
      state,
      countryOfResidenceISO,
    }).unwrap();
  }

  return (
    <div className={styles.ContactDataSection}>
      <h2 className={styles.ContactDataSection__Title}>Información de contacto</h2>
      <div className={styles.ContactDataSection__Grid}>
        <Controller
          control={control}
          name='countryOfResidence'
          render={({ field: { onChange, value, name, ...rest }, fieldState }) => (
            <CountrySelect
              textLabel='País de residencia'
              htmlForLabel='countryOfResidence'
              subtext={fieldState.error?.message}
              placeholder={'Seleccione uno'}
              error={fieldState.error !== undefined}
              handleChange={(value, externalCode, valueEn) => {
                let {countryOfResidence} = getValues()
                if(value != countryOfResidence){
                  setValue('departamentOfResidence', '');
                  setValue('cityOfResidence', '');
                  setValue('documentType', '');
                }                
                setValue('countryOfResidenceISO', externalCode ?? '');
                setValue('countryOfBirthEn', valueEn ?? '');
                trigger(['departamentOfResidence', 'documentType']);
                onChange(value);
                submitData();
              }}
              value={{
                label: value,
                value,
              }}
              onExternalCodeChange={(externalCode) => {
                setCountryOfResidence(externalCode);
              }}
              name={name}
              defaultValue={defaultCountry}
              {...rest}
            />
          )}
        />
        <Controller
          control={control}
          name='departamentOfResidence'
          render={({ field: { onChange, value, name, ...rest }, fieldState }) => (
            <DepartmentSelect
              textLabel='Departamento de residencia'
              htmlForLabel='departamentOfResidence'
              subtext={fieldState.error?.message}
              error={fieldState.error !== undefined}
              placeholder={'Seleccione uno'}
              handleChange={(value, externalCode) => {
                let {departamentOfResidence} = getValues()
                if(value != departamentOfResidence){
                  setValue('cityOfResidence', '');
                }   
                setValue('state', externalCode ?? '');
                trigger(['cityOfResidence']);
                onChange(value);
                submitData();
              }}
              value={{
                label: value,
                value,
              }}
              disabled={!countryOfResidence || isErrorCountryController}
              country={countryOfResidence}
              catalogName={countryStatesCode}
              onExternalCodeChange={(externalCode) => {
                setDepartmentOfResidence(externalCode);
              }}
              name={name}
              defaultValue={defaultState}
              {...rest}
            />
          )}
        />
        <Controller
          control={control}
          name='cityOfResidence'
          render={({ field: { name, onChange, value, ...rest }, fieldState }) => (
            <CitySelect
              textLabel='Ciudad de residencia'
              htmlForLabel='cityOfResidence'
              subtext={fieldState.error?.message}
              error={fieldState.error !== undefined}
              placeholder={'Seleccione uno'}
              handleChange={(value) => {
                onChange(value);
                submitData();
              }}
              value={{
                label: value,
                value,
              }}
              catalogName={countryCitiesCode}
              father={departmentOfResidence}
              disabled={!departmentOfResidence || isErrorCountryController}
              department={departmentOfResidence}
              onExternalCodeChange={(externalCode) => {
                setCityOfResidence(externalCode);
              }}
              name={name}
              defaultValue={defaultCity}
              {...rest}
            />
          )}
        />
        <Controller
          control={control}
          name='addressType'
          defaultValue='Domicilio'
          render={({ field, fieldState: { error } }) => (
            <Select
              textLabel='Tipo de dirección'
              htmlForLabel='Tipo de dirección'
              placeholder='Seleccione una'
              disabled={true}
              options={options}
              error={error !== undefined}
              subtext={error?.message}
              {...field}
              handleChange={(value) => {
                field.onChange(value);
                submitData();
              }}
              value={{
                label: 'Domicilio',
                value: 'Domicilio',
              }}
            />
          )}
        />
        <Controller
          control={control}
          name='address'
          render={({ field: { onBlur, ...field }, fieldState: { error, isTouched } }) => (
            <Input
              onBlur={(e) => {
                onBlur();
                submitData();
              }}
              id='address'
              label='Dirección'
              {...field}
              error={error !== undefined}
              success={isTouched && error === undefined}
              subtext={error?.message}
              maxLength={40}
            />
          )}
        />
        <Controller
          control={control}
          name='complementaryAddresses'
          render={({ field: { onBlur, ...field }, fieldState: { error, isTouched } }) => (
            <Input
              onBlur={(e) => {
                onBlur();
                submitData();
              }}
              id='complementaryAddresses'
              label='Complemento dirección (opcional)'
              {...field}
              error={error !== undefined}
              success={isTouched && error === undefined}
              subtext={error?.message}
              maxLength={40}
            />
          )}
        />
        <Controller
          control={control}
          name='phone'
          render={({ field: { onBlur, ...field }, fieldState: { error, isTouched } }) => (
            <Input
              onBlur={(e) => {
                onBlur();
                submitData();
              }}
              id='phone'
              label='Número de contacto'
              {...field}
              error={error !== undefined}
              success={isTouched && error === undefined}
              subtext={error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name='email'
          render={({ field: { onBlur, ...field }, fieldState: { error, isTouched } }) => (
            <Input
              onBlur={(e) => {
                onBlur();
                submitData();
              }}
              id='email'
              label='Correo personal'
              {...field}
              type='email'
              disabled
              error={error !== undefined}
              success={isTouched && error === undefined}
              subtext={error?.message}
            />
          )}
        />
      </div>
    </div>
  );
}
